import React, { useState, useRef } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ImageUpload - URL input + Browse button + preview
 *
 * Props:
 *  - value: current image URL
 *  - onChange: (newUrl) => void
 *  - label: input label
 *  - placeholder: input placeholder
 *  - testId: optional data-testid prefix
 */
export const ImageUpload = ({ value, onChange, label = 'Şəkil URL', placeholder = 'https://...', testId = 'image' }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Client-side validation
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      setUploadError('Yalnız şəkil faylları qəbul edilir (JPG, PNG, WebP, GIF)');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('Şəkil 5MB-dan böyük ola bilməz');
      return;
    }

    setUploading(true);
    setUploadError('');

    try {
      const formData = new FormData();
      formData.append('file', file);

      const { data } = await axios.post(`${BACKEND_URL}/api/upload/image`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      onChange(data.url);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Yükləmə xətası';
      setUploadError(typeof msg === 'string' ? msg : 'Yükləmə xətası');
    } finally {
      setUploading(false);
      // Reset file input so same file can be reselected
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <div className="flex space-x-2">
        <input
          type="url"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
          placeholder={placeholder}
          data-testid={`${testId}-url-input`}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="px-4 py-2 bg-emerald-100 hover:bg-emerald-200 text-emerald-700 font-medium rounded-lg transition flex items-center space-x-2 whitespace-nowrap disabled:opacity-50"
          data-testid={`${testId}-browse-button`}
        >
          {uploading ? (
            <>
              <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>Yüklənir...</span>
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              <span>Browse</span>
            </>
          )}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          onChange={handleFileSelect}
          className="hidden"
          data-testid={`${testId}-file-input`}
        />
      </div>
      {uploadError && (
        <p className="mt-1 text-xs text-red-600">{uploadError}</p>
      )}
      {value && (
        <div className="mt-2">
          <img
            src={value}
            alt="Preview"
            className="w-24 h-24 object-cover rounded-lg border border-gray-200"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
            data-testid={`${testId}-preview`}
          />
        </div>
      )}
    </div>
  );
};
